import React from 'react';
import { Handle, Position, Node } from '@xyflow/react';
import { AttachMoney, Schedule, Work } from '@mui/icons-material';
import {
  Expense,
  ExpenseMode,
  ExpenseStatus,
} from '../../../../../interfaces/delivery-company/expenses';
import { Chip, Button } from '@mui/material';
import ImportanceBar from '../importance-bar';
import Decimal from 'decimal.js';
import { add, Money } from '../../../../../utils/money';
import { AugmentedExpense } from '../utils';

interface ExpenseNodeProps {
  data: {
    expense?: AugmentedExpense;
    frequency?: number;
    importance?: Decimal;
    isAggregate?: boolean;
    expanded?: boolean;
    onToggleExpand?: () => void;
    label?: string;
  };
}

const modeConfig = {
  MANUAL: {
    icon: <AttachMoney style={{ color: '#1e88e5' }} />,
    gradient: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
    border: '#42a5f5',
    textColor: '#0d47a1',
    background: '#e3f2fd33',
    iconBg: '#bbdefb88',
  },
  RECURRING: {
    icon: <Schedule style={{ color: '#43a047' }} />,
    gradient: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
    border: '#66bb6a',
    textColor: '#1b5e20',
    background: '#e8f5e933',
    iconBg: '#c8e6c988',
  },
  SALARY: {
    icon: <Work style={{ color: '#ffa000' }} />,
    gradient: 'linear-gradient(135deg, #fff8e1, #ffe082)',
    border: '#ffca28',
    textColor: '#ff6f00',
    background: '#fff8e133',
    iconBg: '#ffe08288',
  },
};

export const ExpenseNode = ({ data }: ExpenseNodeProps) => {
  const isAggregate = data.isAggregate;
  const expanded = data.expanded;

  const style = data.expense
    ? modeConfig[data.expense.mode as ExpenseMode] || modeConfig.MANUAL
    : {
        background: 'rgba(255, 255, 255, 0.75)',
        iconBg: '#f5f5f5',
        textColor: '#333',
        icon: <AttachMoney style={{ color: '#555' }} />,
      };

  return (
    <div
      style={{
        background: style.background,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        border: '1px solid #e0f2f1',
        borderRadius: '16px',
        padding: '20px',
        width: 260,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        fontFamily: `'Inter', 'Helvetica Neue', sans-serif`,
        color: '#333',
        position: 'relative',
        userSelect: 'none',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div
          style={{
            backgroundColor: style.iconBg,
            borderRadius: '8px',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
            minWidth: 32,
            minHeight: 32,
          }}
        >
          {style.icon}
        </div>
        <div
          style={{ fontSize: '1rem', fontWeight: 600, color: style.textColor }}
        >
          {isAggregate
            ? data.label || 'Expenses'
            : data.expense?.category?.label || 'Unlabeled'}
        </div>
      </div>

      {/* Content */}
      {isAggregate ? (
        <>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>
            {data.label || 'Expenses group'}
          </div>
          {data.onToggleExpand && (
            <Button
              variant="outlined"
              onClick={data.onToggleExpand}
              size="small"
              sx={{ mt: 2 }}
            >
              {expanded ? 'Collapse' : 'See More'}
            </Button>
          )}
        </>
      ) : (
        <>
          {data.expense!.id > 0 && (
            <div
              style={{
                fontSize: '0.85rem',
                color: style.textColor,
                marginBottom:
                  data.expense?.mode === ExpenseMode.RECURRING ? 6 : 12,
                userSelect: 'text',
              }}
            >
              Mode: <strong>{data.expense?.mode}</strong>
            </div>
          )}

          {data.expense?.mode === ExpenseMode.RECURRING && (
            <Chip
              label={data.expense.recurring_template?.name || 'Recurring'}
              size="small"
              style={{
                backgroundColor: '#e0f2f1',
                color: '#004d40',
                fontWeight: 500,
                marginBottom: 12,
              }}
            />
          )}

          <div
            style={{
              fontSize: '0.85rem',
              color: '#666',
              marginBottom: 12,
            }}
          >
            Occurrences:{' '}
            <span style={{ marginLeft: 4, color: '#999' }}>
              ×{data.expense!.frequency}
            </span>
          </div>

          <div style={{ marginTop: 4 }}>
            <ImportanceBar
              importance={data.importance ?? new Decimal(0)}
              color="red"
            />
          </div>
        </>
      )}

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-sink"
        style={{ opacity: 0 }}
      />

      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-source"
        style={{ opacity: 0 }}
      />
    </div>
  );
};
